// SPDX-License-Identifier: MIT
/**
 * RFC 6902 JSON Patch on plain JSON values. Shared by the Player (instant edit preview) and the API
 * (FR-7.2 manual edits), so both apply an edit identically. The input is never mutated.
 */
import type { JsonPatch } from './player.ts';

export class JsonPatchError extends Error {
  readonly index: number;
  constructor(message: string, index: number) {
    super(`patch op ${index}: ${message}`);
    this.index = index;
  }
}

type Json = unknown;
type Container = Record<string, Json> | Json[];

/** Parse a JSON Pointer (RFC 6901) into unescaped tokens. */
export function parsePointer(pointer: string): string[] {
  if (pointer === '') return [];
  if (!pointer.startsWith('/')) throw new Error(`invalid JSON pointer "${pointer}"`);
  return pointer
    .slice(1)
    .split('/')
    .map((t) => t.replace(/~1/g, '/').replace(/~0/g, '~'));
}

const isContainer = (v: Json): v is Container => typeof v === 'object' && v !== null;
const UNSAFE = new Set(['__proto__', 'constructor', 'prototype']);

function arrayIndex(arr: Json[], token: string, forAdd: boolean): number {
  if (forAdd && token === '-') return arr.length;
  if (!/^(0|[1-9][0-9]*)$/.test(token)) throw new Error(`"${token}" is not an array index`);
  const i = Number(token);
  if (i > arr.length || (!forAdd && i === arr.length)) throw new Error(`index ${i} is out of bounds`);
  return i;
}

/** The container holding the pointer's last token, plus that token. */
function locate(doc: Json, tokens: string[]): [Container, string] {
  let node = doc;
  for (const t of tokens.slice(0, -1)) {
    if (!isContainer(node)) throw new Error('path goes through a non-container');
    if (Array.isArray(node)) node = node[arrayIndex(node, t, false)];
    else {
      if (!Object.hasOwn(node, t)) throw new Error(`"${t}" does not exist`);
      node = node[t];
    }
  }
  if (!isContainer(node)) throw new Error('parent is not a container');
  return [node, tokens[tokens.length - 1]!];
}

function get(doc: Json, tokens: string[]): Json {
  if (tokens.length === 0) return doc;
  const [parent, key] = locate(doc, tokens);
  if (Array.isArray(parent)) return parent[arrayIndex(parent, key, false)];
  if (!Object.hasOwn(parent, key)) throw new Error(`"${key}" does not exist`);
  return parent[key];
}

function remove(doc: Json, tokens: string[]): Json {
  if (tokens.length === 0) throw new Error('cannot remove the whole document');
  const [parent, key] = locate(doc, tokens);
  if (Array.isArray(parent)) return parent.splice(arrayIndex(parent, key, false), 1)[0];
  if (!Object.hasOwn(parent, key)) throw new Error(`"${key}" does not exist`);
  const v = parent[key];
  delete parent[key];
  return v;
}

function add(doc: Json, tokens: string[], value: Json): Json {
  if (tokens.length === 0) return value;
  const [parent, key] = locate(doc, tokens);
  if (Array.isArray(parent)) parent.splice(arrayIndex(parent, key, true), 0, value);
  else {
    if (UNSAFE.has(key)) throw new Error(`"${key}" is not allowed`);
    parent[key] = value;
  }
  return doc;
}

const equal = (a: Json, b: Json): boolean => JSON.stringify(a) === JSON.stringify(b);

/** Apply `patch` to a copy of `doc`. Throws `JsonPatchError` (and changes nothing) if any op fails. */
export function applyJsonPatch<T>(doc: T, patch: JsonPatch): T {
  let out: Json = structuredClone(doc);
  patch.forEach((op, i) => {
    try {
      const path = parsePointer(op.path);
      switch (op.op) {
        case 'add':
          out = add(out, path, structuredClone(op.value));
          break;
        case 'remove':
          remove(out, path);
          break;
        case 'replace':
          get(out, path);
          if (path.length === 0) out = structuredClone(op.value);
          else {
            remove(out, path);
            out = add(out, path, structuredClone(op.value));
          }
          break;
        case 'move': {
          const from = parsePointer(op.from);
          if (op.path.startsWith(`${op.from}/`)) throw new Error('cannot move a value into itself');
          out = add(out, path, remove(out, from));
          break;
        }
        case 'copy':
          out = add(out, path, structuredClone(get(out, parsePointer(op.from))));
          break;
        case 'test':
          if (!equal(get(out, path), op.value)) throw new Error('test failed');
          break;
      }
    } catch (err) {
      if (err instanceof JsonPatchError) throw err;
      throw new JsonPatchError((err as Error).message, i);
    }
  });
  return out as T;
}
